package fr.manyverse.app.presentation.components.modules.whiteboard

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Button
import androidx.compose.material3.LinearProgressIndicator
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import androidx.compose.ui.window.DialogProperties
import fr.manyverse.app.domain.model.TableMember
import fr.manyverse.app.presentation.components.CheckboxRow
import fr.manyverse.app.presentation.components.modules.common.ParticipantSelectionMode
import fr.manyverse.app.presentation.components.modules.common.ParticipantSelector
import manyverse.shared.generated.resources.Res
import manyverse.shared.generated.resources.cancel
import manyverse.shared.generated.resources.whiteboard_open
import manyverse.shared.generated.resources.whiteboard_open_title
import manyverse.shared.generated.resources.whiteboard_show_grid_option
import org.jetbrains.compose.resources.stringResource

/**
 * Dialog for configuring and launching a whiteboard.
 */
@Composable
fun WhiteboardCreationDialog(
    showDialog: Boolean,
    players: List<TableMember>,
    labelOf: (TableMember) -> String = { it.userId.take(8) },
    selectedParticipantIds: Set<String>,
    allSelected: Boolean,
    isLoading: Boolean,
    errorMessage: String?,
    onToggleAllSelected: () -> Unit,
    onToggleParticipant: (String) -> Unit,
    showGrid: Boolean,
    onToggleShowGrid: () -> Unit,
    onDismiss: () -> Unit,
    onConfirm: () -> Unit,
    modifier: Modifier = Modifier
) {
    val canSubmit = (allSelected || selectedParticipantIds.isNotEmpty()) && !isLoading

    if (showDialog) {
        AlertDialog(
            properties = DialogProperties(dismissOnBackPress = false, dismissOnClickOutside = false),
            onDismissRequest = {
                if (!isLoading) {
                    onDismiss()
                }
            },
            title = {
                Text(stringResource(Res.string.whiteboard_open_title))
            },
            text = {
                Column(
                    modifier = Modifier.fillMaxWidth(),
                    verticalArrangement = Arrangement.spacedBy(8.dp)
                ) {
                    // Participants selection
                    ParticipantSelector(
                        players = players,
                        mode = ParticipantSelectionMode.Multiple(
                            selectedIds = selectedParticipantIds,
                            allSelected = allSelected,
                            onToggle = onToggleParticipant,
                            onToggleAll = onToggleAllSelected
                        ),
                        labelOf = labelOf,
                        isEnabled = !isLoading
                    )

                    // Show grid option
                    CheckboxRow(
                        label = stringResource(Res.string.whiteboard_show_grid_option),
                        checked = showGrid,
                        onCheckedChange = { onToggleShowGrid() },
                        enabled = !isLoading
                    )

                    // Error message
                    if (errorMessage != null) {
                        Text(
                            text = errorMessage,
                            color = MaterialTheme.colorScheme.error,
                            style = MaterialTheme.typography.bodySmall
                        )
                    }

                    // Loading indicator
                    if (isLoading) {
                        LinearProgressIndicator(
                            modifier = Modifier.fillMaxWidth()
                        )
                    }
                }
            },
            confirmButton = {
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    TextButton(
                        onClick = onDismiss,
                        enabled = !isLoading
                    ) {
                        Text(stringResource(Res.string.cancel))
                    }
                    Button(
                        onClick = onConfirm,
                        enabled = canSubmit
                    ) {
                        Text(stringResource(Res.string.whiteboard_open))
                    }
                }
            },
            modifier = modifier
        )
    }
}
